import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { format } from 'date-fns';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import SignatureCanvas from 'react-signature-canvas';
import { api } from '../../lib/api';
import { useOrg } from '../../hooks/useOrg';
import LoadingSpinner from '../shared/LoadingSpinner';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const MOBILE_BREAKPOINT = 640;

export default function FormSigningPage() {
  const { id } = useParams();
  const { memberships } = useOrg();
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const sigRefs = useRef({});
  const fieldRefs = useRef({});

  const stateOrgId = routerLocation.state?.orgId;
  const queryOrgId = new URLSearchParams(routerLocation.search).get('org');
  const initialOrgId = stateOrgId || queryOrgId || null;
  const [orgIdForForm, setOrgIdForForm] = useState(initialOrgId);

  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fieldValues, setFieldValues] = useState({});
  const [signing, setSigning] = useState(false);
  const [signed, setSigned] = useState(false);
  const [error, setError] = useState('');
  const [numPages, setNumPages] = useState(0);
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 800);
  const [currentStep, setCurrentStep] = useState(0);

  const isMobile = windowWidth < MOBILE_BREAKPOINT;

  const fetchForm = useCallback(async () => {
    const candidates = [];
    if (orgIdForForm) candidates.push(orgIdForForm);
    for (const m of memberships || []) {
      if (m.org_id && !candidates.includes(m.org_id)) candidates.push(m.org_id);
    }
    if (!candidates.length) { setLoading(false); return; }

    for (const orgId of candidates) {
      try {
        const data = await api.getFormForSigning(orgId, id);
        setForm(data);
        setFieldValues(data.field_values || {});
        setOrgIdForForm(orgId);
        setLoading(false);
        return;
      } catch {
        // try next membership
      }
    }
    setError('Form not found');
    setLoading(false);
  }, [orgIdForForm, memberships, id]);

  useEffect(() => { fetchForm(); }, [fetchForm]);

  useEffect(() => {
    const handler = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  const updateField = (fieldId, value) => setFieldValues(prev => ({ ...prev, [fieldId]: value }));

  // Toggle an option within a multiple/radio field.
  //   radio    → value holds a single option id (or null to clear)
  //   multiple → value holds an array of option ids
  const toggleOption = (field, optionId, checked) => {
    setFieldValues(prev => {
      if (field.type === 'radio') {
        return { ...prev, [field.id]: checked ? optionId : null };
      }
      const cur = Array.isArray(prev[field.id]) ? prev[field.id] : [];
      const next = checked ? Array.from(new Set([...cur, optionId])) : cur.filter(o => o !== optionId);
      return { ...prev, [field.id]: next };
    });
  };

  const isOptionSelected = (field, optionId) => {
    const v = fieldValues[field.id];
    if (field.type === 'radio') return v === optionId;
    if (field.type === 'multiple') return Array.isArray(v) && v.includes(optionId);
    return false;
  };

  const handleSign = async () => {
    const template = form?.template;
    const placements = template?.field_placements || [];

    for (const field of placements) {
      if (!field.required) continue;
      if (field.type === 'multiple' || field.type === 'radio') {
        const v = fieldValues[field.id];
        const hasAny = field.type === 'radio' ? !!v : Array.isArray(v) && v.length > 0;
        if (!hasAny) {
          setError(`"${field.label}" is required`);
          return;
        }
      } else if (field.type === 'checkbox') {
        if (!fieldValues[field.id]) {
          setError(`"${field.label}" is required`);
          return;
        }
      } else if (field.type !== 'signature' && field.type !== 'initials') {
        const val = fieldValues[field.id];
        if (!val || (typeof val === 'string' && !val.trim())) {
          setError(`"${field.label}" is required`);
          return;
        }
      }
    }

    const sigFields = placements.filter(f => f.type === 'signature' || f.type === 'initials');
    const signatureData = {};
    for (const sf of sigFields) {
      const ref = sigRefs.current[sf.id];
      const stored = fieldValues[sf.id];
      const hasLiveSig = ref && !ref.isEmpty();
      const hasStoredSig = typeof stored === 'string' && stored.startsWith('data:image');
      if (sf.required && !hasLiveSig && !hasStoredSig) {
        setError(`Please provide your ${sf.type === 'initials' ? 'initials' : 'signature'} for "${sf.label}"`);
        return;
      }
      if (hasLiveSig) {
        signatureData[sf.id] = ref.toDataURL('image/png');
      } else if (hasStoredSig) {
        signatureData[sf.id] = stored;
      }
    }

    setError('');
    setSigning(true);

    try {
      await api.signForm(orgIdForForm, id, {
        field_values: { ...fieldValues, ...signatureData },
        signature_data: Object.values(signatureData)[0] || '',
      });
      setSigned(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSigning(false);
    }
  };

  if (loading) return <div className="flex-1 flex items-center justify-center bg-primary"><LoadingSpinner size="lg" /></div>;

  if (signed) {
    return (
      <div className="flex-1 bg-primary flex items-center justify-center p-4">
        <div className="max-w-[420px] text-center">
          <div className="w-[52px] h-[52px] bg-success-soft rounded-[14px] flex items-center justify-center mx-auto mb-4">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
          </div>
          <h2 className="text-[20px] font-bold text-primary-c mb-2">Document Signed</h2>
          <p className="text-[13px] text-muted-c mb-6">Your signed document has been submitted and recorded with a tamper-proof hash.</p>
          <button onClick={() => navigate('/client/forms')} className="px-5 py-2.5 rounded-[10px] text-[13px] font-semibold text-white gradient-accent shadow-glow hover:-translate-y-0.5 transition-all">
            Back to My Forms
          </button>
        </div>
      </div>
    );
  }

  if (!form || form.status === 'signed') {
    return (
      <div className="flex-1 bg-primary flex items-center justify-center p-4">
        <div className="text-center">
          <h2 className="text-[18px] font-bold text-primary-c mb-2">{form?.status === 'signed' ? 'Already Signed' : 'Form Not Found'}</h2>
          <button onClick={() => navigate('/client/forms')} className="text-[13px] text-accent-light hover:underline mt-2">Back to My Forms</button>
        </div>
      </div>
    );
  }

  const template = form.template;
  const placements = template?.field_placements || [];
  const hasDocument = !!template?.document_url;

  if (!hasDocument) {
    // Fallback — legacy form rendering, no PDF uploaded. Very rarely used.
    return (
      <div className="flex-1 bg-primary flex items-center justify-center p-4">
        <div className="text-center">
          <h2 className="text-[18px] font-bold text-primary-c mb-2">This form has no document</h2>
          <button onClick={() => navigate('/client/forms')} className="text-[13px] text-accent-light hover:underline mt-2">Back to My Forms</button>
        </div>
      </div>
    );
  }

  return (
    <GuidedSigningFlow
      template={template}
      placements={placements}
      form={form}
      numPages={numPages}
      setNumPages={setNumPages}
      pageWidth={isMobile ? Math.min(windowWidth, 700) : Math.min(windowWidth - 48, 820)}
      isMobile={isMobile}
      fieldValues={fieldValues}
      updateField={updateField}
      toggleOption={toggleOption}
      isOptionSelected={isOptionSelected}
      sigRefs={sigRefs}
      fieldRefs={fieldRefs}
      currentStep={currentStep}
      setCurrentStep={setCurrentStep}
      error={error}
      setError={setError}
      signing={signing}
      onSign={handleSign}
      onBack={() => navigate('/client/forms')}
    />
  );
}

function GuidedSigningFlow({
  template, placements, form, numPages, setNumPages, pageWidth, isMobile,
  fieldValues, updateField, toggleOption, isOptionSelected, sigRefs, fieldRefs,
  currentStep, setCurrentStep, error, setError, signing, onSign, onBack,
}) {
  // Top-level fields in document order. Each top-level field is one step;
  // multiple/radio fields show all their options in the card.
  const ordered = React.useMemo(() => {
    return (placements || []).slice().sort((a, b) => {
      const aAnchor = (a.options && a.options[0]) || a;
      const bAnchor = (b.options && b.options[0]) || b;
      if (aAnchor.page !== bAnchor.page) return aAnchor.page - bAnchor.page;
      if (Math.abs(aAnchor.y - bAnchor.y) > 2) return aAnchor.y - bAnchor.y;
      return aAnchor.x - bAnchor.x;
    });
  }, [placements]);

  const total = ordered.length;
  const currentField = ordered[currentStep];

  // Scroll the active field (or first option) into view on step change
  React.useEffect(() => {
    const anchorId = currentField
      ? (currentField.options && currentField.options[0]?.id) || currentField.id
      : null;
    const node = anchorId ? fieldRefs.current[anchorId] : null;
    if (node && typeof node.scrollIntoView === 'function') {
      node.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    setError('');
    // eslint-disable-next-line
  }, [currentStep]);

  const validateCurrent = () => {
    const f = currentField;
    if (!f) return true;
    if (!f.required) return true;

    if (f.type === 'signature' || f.type === 'initials') {
      const ref = sigRefs.current[f.id];
      if (!ref || ref.isEmpty()) {
        setError(`Please provide your ${f.type === 'initials' ? 'initials' : 'signature'}`);
        return false;
      }
      updateField(f.id, ref.toDataURL('image/png'));
      return true;
    }

    if (f.type === 'radio') {
      if (!fieldValues[f.id]) {
        setError(`Please select an option for "${f.label}"`);
        return false;
      }
      return true;
    }

    if (f.type === 'multiple') {
      const v = fieldValues[f.id];
      if (!Array.isArray(v) || v.length === 0) {
        setError(`Please select at least one option for "${f.label}"`);
        return false;
      }
      return true;
    }

    if (f.type === 'checkbox') {
      if (!fieldValues[f.id]) {
        setError(`"${f.label || 'Checkbox'}" is required`);
        return false;
      }
      return true;
    }

    const v = fieldValues[f.id];
    if (!v || (typeof v === 'string' && !v.trim())) {
      setError(`"${f.label || 'This field'}" is required`);
      return false;
    }
    return true;
  };

  const goNext = () => {
    if (!validateCurrent()) return;
    if (currentStep < total - 1) setCurrentStep(currentStep + 1);
    else onSign();
  };
  const goPrev = () => { if (currentStep > 0) setCurrentStep(currentStep - 1); };

  const isLast = currentStep === total - 1;
  const isSigField = currentField && (currentField.type === 'signature' || currentField.type === 'initials');
  const isGroupField = currentField && (currentField.type === 'multiple' || currentField.type === 'radio');
  const cardMaxWidth = isSigField ? 520 : isGroupField ? 480 : 440;

  // Collect every shape (top-level field or option) to render on each page
  const shapesByPage = React.useMemo(() => {
    const map = new Map();
    for (const f of ordered) {
      if (f.type === 'multiple' || f.type === 'radio') {
        for (const o of (f.options || [])) {
          const arr = map.get(o.page) || [];
          arr.push({ kind: 'option', field: f, option: o });
          map.set(o.page, arr);
        }
      } else {
        const arr = map.get(f.page) || [];
        arr.push({ kind: 'field', field: f });
        map.set(f.page, arr);
      }
    }
    return map;
  }, [ordered]);

  return (
    <div className="flex-1 flex flex-col bg-[#1E293B]" style={{ minHeight: '100vh' }}>
      <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4">
        <button
          onClick={onBack}
          className="text-[12px] sm:text-[13px] font-medium text-white/70 hover:text-white transition-colors flex items-center gap-1.5"
        >
          &larr; Back
        </button>
        <div className="text-center flex-1 min-w-0 px-4">
          <div className="text-[14px] sm:text-[16px] font-bold text-white truncate">{template?.name}</div>
          {total > 0 && (
            <div className="text-[10px] sm:text-[11px] text-white/60 mt-0.5">Step {currentStep + 1} of {total}</div>
          )}
        </div>
        <div className="w-[50px]" />
      </div>

      <div
        className="flex-1 overflow-y-auto px-3 sm:px-6"
        style={{ WebkitOverflowScrolling: 'touch', paddingBottom: isSigField ? 320 : isGroupField ? 320 : 220 }}
      >
        <div className="mx-auto" style={{ maxWidth: pageWidth }}>
          <Document file={template.document_url} onLoadSuccess={({ numPages: n }) => setNumPages(n)}>
            {Array.from({ length: numPages }, (_, i) => i + 1).map(pageNum => (
              <div
                key={pageNum}
                className="relative mb-4 shadow-[0_8px_28px_rgba(0,0,0,0.35)] rounded-[6px] overflow-hidden bg-white mx-auto"
                style={{ width: 'fit-content' }}
              >
                <Page pageNumber={pageNum} width={pageWidth} renderTextLayer={false} renderAnnotationLayer={false} />

                {(shapesByPage.get(pageNum) || []).map(shape => {
                  if (shape.kind === 'field') {
                    const f = shape.field;
                    const isCurrent = currentField?.id === f.id;
                    const hasValue =
                      (f.type === 'signature' || f.type === 'initials')
                        ? !!fieldValues[f.id]
                        : f.type === 'checkbox'
                          ? !!fieldValues[f.id]
                          : !!(fieldValues[f.id] && String(fieldValues[f.id]).trim());
                    const isCheckbox = f.type === 'checkbox';
                    return (
                      <FieldOverlay
                        key={f.id}
                        f={f}
                        isCurrent={isCurrent}
                        hasValue={hasValue}
                        isCheckbox={isCheckbox}
                        value={fieldValues[f.id]}
                        refCb={el => { if (el) fieldRefs.current[f.id] = el; }}
                        showBadge={isCurrent}
                      />
                    );
                  }

                  // Option hit-area for a multiple/radio field
                  const { field, option } = shape;
                  const isCurrent = currentField?.id === field.id;
                  const selected = isOptionSelected(field, option.id);
                  const isAnchor = isCurrent && field.options[0]?.id === option.id;
                  return (
                    <OptionOverlay
                      key={option.id}
                      option={option}
                      field={field}
                      isCurrent={isCurrent}
                      selected={selected}
                      refCb={el => { if (el) fieldRefs.current[option.id] = el; }}
                      showGroupBadge={isAnchor}
                    />
                  );
                })}
              </div>
            ))}
          </Document>
        </div>
      </div>

      <div className="fixed left-0 right-0 bottom-4 sm:bottom-6 z-20 pointer-events-none px-3">
        <div
          className="pointer-events-auto mx-auto bg-white rounded-[14px] shadow-[0_20px_50px_rgba(0,0,0,0.45)] border border-[#E5E7EB] overflow-hidden"
          style={{ maxWidth: cardMaxWidth }}
        >
          <div className="px-5 pt-4 pb-3">
            {error && (
              <div className="mb-3 px-3.5 py-2.5 rounded-[10px] bg-[#FEE2E2] text-[#B91C1C] text-[12px] font-medium">
                {error}
              </div>
            )}
            {currentField ? (
              isGroupField ? (
                <GroupInput
                  field={currentField}
                  isSelected={optId => isOptionSelected(currentField, optId)}
                  onToggle={(optId, checked) => toggleOption(currentField, optId, checked)}
                />
              ) : (
                <MobileFieldInput
                  field={currentField}
                  value={fieldValues[currentField.id]}
                  onChange={v => updateField(currentField.id, v)}
                  appointmentDate={form.appointment_date}
                  sigRefs={sigRefs}
                />
              )
            ) : (
              <p className="text-[13px] text-[#6B7280] text-center py-4">No fields to fill — submit to sign.</p>
            )}
          </div>

          <div className="px-5 pb-4 flex gap-2">
            {currentStep > 0 && (
              <button
                onClick={goPrev}
                className="px-5 py-3 rounded-[10px] text-[13px] font-semibold text-[#374151] bg-[#F3F4F6] border border-[#E5E7EB] hover:bg-[#E5E7EB] transition-all"
              >
                Back
              </button>
            )}
            <button
              onClick={goNext}
              disabled={signing}
              className="flex-1 py-3 rounded-[10px] text-[14px] font-bold text-white bg-[#1E293B] hover:bg-[#0F172A] disabled:opacity-50 transition-all"
            >
              {isLast ? (signing ? 'Submitting\u2026' : 'Sign & Submit') : 'Next'}
            </button>
          </div>

          {total > 0 && (
            <div className="flex justify-center gap-1.5 pb-3">
              {ordered.map((_, i) => (
                <div
                  key={i}
                  className={`h-1.5 rounded-full transition-all ${
                    i === currentStep ? 'w-5 bg-[#1E293B]' : i < currentStep ? 'w-1.5 bg-[#10B981]' : 'w-1.5 bg-[#E5E7EB]'
                  }`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function FieldOverlay({ f, isCurrent, hasValue, isCheckbox, value, refCb, showBadge }) {
  return (
    <div
      ref={refCb}
      className="absolute transition-all"
      style={{
        left: `${f.x}%`, top: `${f.y}%`,
        width: `${f.width}%`, height: `${f.height}%`,
        ...(isCheckbox ? {} : { minWidth: 40, minHeight: 18 }),
        border: isCurrent
          ? '1.5px solid #EC4899'
          : hasValue
            ? '1.5px solid rgba(16, 185, 129, 0.8)'
            : isCheckbox
              ? '1px solid rgba(107, 114, 128, 0.65)'
              : '1.5px dashed rgba(236, 72, 153, 0.45)',
        background: isCurrent
          ? 'rgba(236, 72, 153, 0.14)'
          : isCheckbox
            ? 'transparent'
            : hasValue
              ? 'rgba(16, 185, 129, 0.10)'
              : 'rgba(236, 72, 153, 0.08)',
        borderRadius: isCheckbox ? 1 : 3,
        boxShadow: isCurrent ? '0 0 0 3px rgba(236, 72, 153, 0.22)' : 'none',
      }}
    >
      {hasValue && (f.type === 'signature' || f.type === 'initials') && value && (
        <img src={value} alt="" className="w-full h-full object-contain" />
      )}
      {hasValue && !['signature', 'initials', 'checkbox'].includes(f.type) && (
        <div className="w-full h-full flex items-center px-1 text-[10px] sm:text-[11px] text-[#111827] font-medium overflow-hidden whitespace-nowrap">
          {f.type === 'date'
            ? (() => {
                const d = new Date(value);
                return isNaN(d.getTime()) ? value : format(d, 'MM/dd/yyyy');
              })()
            : String(value)}
        </div>
      )}
      {hasValue && isCheckbox && (
        <svg viewBox="0 0 24 24" className="w-full h-full" fill="none" stroke="#111827" strokeWidth="4" strokeLinecap="round">
          <path d="M5 5 L19 19 M19 5 L5 19" />
        </svg>
      )}
      {showBadge && (
        <span className="absolute -top-[18px] left-0 text-[9px] font-bold uppercase tracking-wide text-white bg-[#EC4899] px-1.5 py-[2px] rounded-[3px] whitespace-nowrap">
          {f.label || f.type}
        </span>
      )}
    </div>
  );
}

function OptionOverlay({ option, field, isCurrent, selected, refCb, showGroupBadge }) {
  return (
    <div
      ref={refCb}
      className="absolute"
      style={{
        left: `${option.x}%`, top: `${option.y}%`,
        width: `${option.width}%`, height: `${option.height}%`,
        border: isCurrent
          ? '1.5px solid #EC4899'
          : selected
            ? '1.5px solid rgba(16, 185, 129, 0.85)'
            : '1px solid rgba(107, 114, 128, 0.65)',
        background: isCurrent
          ? 'rgba(236, 72, 153, 0.14)'
          : selected
            ? 'rgba(16, 185, 129, 0.10)'
            : 'transparent',
        borderRadius: 1,
        boxShadow: isCurrent ? '0 0 0 3px rgba(236, 72, 153, 0.22)' : 'none',
      }}
    >
      {selected && (
        <svg viewBox="0 0 24 24" className="w-full h-full" fill="none" stroke="#111827" strokeWidth="4" strokeLinecap="round">
          <path d="M5 5 L19 19 M19 5 L5 19" />
        </svg>
      )}
      {showGroupBadge && (
        <span className="absolute -top-[18px] left-0 text-[9px] font-bold uppercase tracking-wide text-white bg-[#EC4899] px-1.5 py-[2px] rounded-[3px] whitespace-nowrap">
          {field.label}
        </span>
      )}
    </div>
  );
}

function GroupInput({ field, isSelected, onToggle }) {
  const { type, label, required, options = [] } = field;
  return (
    <div>
      <div className="mb-2.5">
        <div className="text-[14px] font-bold text-[#111827]">
          {label}{required && <span className="text-[#EC4899] ml-1">*</span>}
        </div>
        <div className="text-[11px] text-[#6B7280] mt-0.5">
          {type === 'radio' ? 'Select one' : 'Select all that apply'}
        </div>
      </div>
      <div className="max-h-[42vh] overflow-y-auto -mx-1 px-1 space-y-0.5">
        {options.map(opt => {
          const checked = isSelected(opt.id);
          return (
            <label
              key={opt.id}
              className="flex items-center gap-2.5 py-2 px-2 cursor-pointer hover:bg-[#F9FAFB] rounded-md select-none"
            >
              <input
                type={type === 'radio' ? 'radio' : 'checkbox'}
                name={`group-${field.id}`}
                checked={checked}
                onChange={e => onToggle(opt.id, e.target.checked)}
                className="w-4 h-4 accent-[#111827] flex-shrink-0"
              />
              <span className="text-[13px] text-[#111827]">{opt.label || 'Option'}</span>
            </label>
          );
        })}
      </div>
    </div>
  );
}

function MobileFieldInput({ field, value, onChange, appointmentDate, sigRefs }) {
  const label = field.label || (field.type === 'signature' ? 'Signature' : field.type === 'initials' ? 'Initials' : 'Field');

  if (field.type === 'signature' || field.type === 'initials') {
    return (
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-[13px] font-semibold text-[#111827]">
            {label}{field.required && <span className="text-[#EC4899] ml-1">*</span>}
          </label>
          <button
            type="button"
            onClick={() => sigRefs.current[field.id]?.clear()}
            className="text-[11px] text-[#6B7280]"
          >
            Clear
          </button>
        </div>
        <div className="border-2 border-dashed border-[#D1D5DB] rounded-[10px] overflow-hidden bg-white touch-none">
          <SignatureCanvas
            ref={r => { sigRefs.current[field.id] = r; }}
            penColor="#111827"
            canvasProps={{ style: { width: '100%', height: field.type === 'initials' ? 110 : 150, display: 'block' } }}
          />
        </div>
        <p className="text-[10px] text-[#6B7280] mt-1.5">Sign with your finger inside the box.</p>
      </div>
    );
  }

  if (field.type === 'checkbox') {
    return (
      <label className="flex items-center gap-3 py-2 cursor-pointer select-none">
        <input
          type="checkbox"
          checked={!!value}
          onChange={e => onChange(e.target.checked)}
          className="w-5 h-5 accent-[#111827]"
        />
        <span className="text-[14px] text-[#111827]">
          {label}{field.required && <span className="text-[#EC4899] ml-1">*</span>}
        </span>
      </label>
    );
  }

  const isAutoDate = field.type === 'date' && field.auto_fill === 'appointment_date';
  const displayedValue = isAutoDate && appointmentDate ? format(new Date(appointmentDate), 'MM/dd/yyyy') : (value || '');

  React.useEffect(() => {
    if (isAutoDate && appointmentDate && !value) {
      onChange(new Date(appointmentDate).toISOString());
    }
    // eslint-disable-next-line
  }, [field.id]);

  return (
    <div>
      <label className="block text-[12px] font-semibold text-[#374151] mb-1.5">
        {label}{field.required && <span className="text-[#EC4899] ml-1">*</span>}
      </label>
      <input
        type={field.type === 'date' ? 'date' : 'text'}
        value={isAutoDate ? displayedValue : (value || '')}
        onChange={e => onChange(e.target.value)}
        readOnly={isAutoDate}
        placeholder={`Type ${label.toLowerCase()}\u2026`}
        className={`w-full px-4 py-3.5 bg-white border-2 border-[#E5E7EB] rounded-[12px] text-[16px] text-[#111827] font-medium focus:outline-none focus:border-[#111827] transition-all ${isAutoDate ? 'bg-[#F9FAFB]' : ''}`}
      />
      {isAutoDate && <p className="text-[10px] text-[#6B7280] mt-1">Auto-filled from your appointment</p>}
    </div>
  );
}
